import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import PaymentHistory from './PaymentHistory';
import NewPayment from './NewPayment';
import Admin from './Admin';
import Reports from './Reports';

const API = process.env.REACT_APP_API_URL;
export { API };

const ADMIN_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || '').toLowerCase();

function LoadingScreen({ message }) {
  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#0f0f0f', minHeight: '100vh', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>
      {message || 'Loading...'}
    </div>
  );
}

function LoginPage({ onLogin }) {
  const [mode, setMode]               = useState('login');   // 'login' | 'forgot' | 'sent'
  const [email, setEmail]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const card = { background: '#161616', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: '40px 36px', width: 320 };
  const btn  = (extra = {}) => ({
    border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 13, fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', width: '100%', ...extra,
  });
  const linkBtn = { background: 'none', border: 'none', color: '#555', fontSize: 12,
    fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', padding: 0, textDecoration: 'underline' };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Enter your email address.'); return; }
    setError('');
    setLoading(true);
    try {
      await fetch(`${API}/api/auth/send-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      setMode('sent');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const logo = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 600, letterSpacing: '-0.3px', marginBottom: 32 }}>
      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80' }} />
      ROW Payment Tool
      <span style={{ fontSize: 11, color: '#555', fontWeight: 400, marginLeft: 4 }}>by Sway Integrations</span>
    </div>
  );

  if (mode === 'sent') {
    return (
      <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#0f0f0f', minHeight: '100vh', color: '#f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          {logo}
          <div style={card}>
            <div style={{ fontSize: 22, marginBottom: 12 }}>📧</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Check your email</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 24 }}>
              If an account exists for <strong style={{ color: '#f0ede8' }}>{email}</strong>, a reset link is on its way. It expires in 24 hours.
            </div>
            <button onClick={() => { setMode('login'); setEmail(''); }} style={btn({ background: '#242424', color: '#f0ede8' })}>
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'forgot') {
    return (
      <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#0f0f0f', minHeight: '100vh', color: '#f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          {logo}
          <div style={card}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Reset your password</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 24 }}>Enter your account email and we'll send a reset link.</div>
            <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com" autoFocus required
                style={{ padding: '10px 12px', background: '#111', border: '0.5px solid #2a2a2a', borderRadius: 8, color: '#f0ede8', fontSize: 13, fontFamily: "'DM Sans', sans-serif", outline: 'none' }}
              />
              {error && <div style={{ fontSize: 11, color: '#f87171', textAlign: 'left' }}>{error}</div>}
              <button type="submit" disabled={loading} style={btn({ background: loading ? '#1a3a2a' : '#4ade80', color: '#0f0f0f' })}>
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>
            <div style={{ marginTop: 16 }}>
              <button onClick={() => { setMode('login'); setError(''); }} style={linkBtn}>← Back to sign in</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#0f0f0f', minHeight: '100vh', color: '#f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        {logo}
        <div style={card}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Sign in to your account</div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 28 }}>Enter your email and password to access the payment portal</div>
          <button onClick={onLogin} style={btn({ background: '#4ade80', color: '#0f0f0f', marginBottom: 16 })}>
            Sign In
          </button>
          <div>
            <button onClick={() => setMode('forgot')} style={linkBtn}>Forgot password?</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { isLoading, isAuthenticated, user, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();

  const [tab, setTab] = useState('history');
  // undefined = loading, null = admin (no shop needed), number = shopId
  const [shopId, setShopId] = useState(undefined);
  const [shopError, setShopError] = useState(null);

  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL;

  const getToken = useCallback(async () => {
    return await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: process.env.REACT_APP_AUTH0_SCOPE,
      },
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    if (isAdmin) {
      setShopId(null);
      return;
    }

    setShopId(undefined);
    setShopError(null);

    getToken()
      .then(token =>
        fetch(`${API}/api/me/shop`, { headers: { Authorization: `Bearer ${token}` } })
      )
      .then(async r => {
        if (!r.ok) {
          const b = await r.json().catch(() => ({}));
          throw new Error(b.error || 'Access denied');
        }
        return r.json();
      })
      .then(data => setShopId(data.ShopId))
      .catch(err => {
        setShopError(
          err.message.includes('No shop')
            ? `Your account is not linked to any shop. Contact ${process.env.REACT_APP_ADMIN_EMAIL}.`
            : 'Failed to load your account. Please sign out and try again.'
        );
        setShopId(null);
      });
  }, [isAuthenticated, isLoading, isAdmin, getToken]);

  const handleLogin = () => loginWithRedirect();
  const handleLogout = () => logout({ logoutParams: { returnTo: window.location.origin } });

  if (isLoading) return <LoadingScreen message="Loading..." />;
  if (!isAuthenticated) return <LoginPage onLogin={handleLogin} />;
  if (shopId === undefined && !shopError) return <LoadingScreen message="Loading your account..." />;

  const navBtn = (key, label) => (
    <button key={key} onClick={() => setTab(key)} style={{
      background: tab === key ? '#242424' : 'none',
      border: 'none',
      color: tab === key ? '#f0ede8' : '#666',
      fontFamily: "'DM Sans', sans-serif",
      fontSize: 13,
      padding: '5px 14px',
      borderRadius: 6,
      cursor: 'pointer',
      transition: 'all 0.15s',
    }}>{label}</button>
  );

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#0f0f0f', minHeight: '100vh', color: '#f0ede8' }}>
      {/* Nav */}
      <div style={{ background: '#111', borderBottom: '0.5px solid #222', padding: '0 20px', height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, letterSpacing: '-0.3px' }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80' }} />
          ROW Payment Tool
          <span style={{ fontSize: 11, color: '#555', fontWeight: 400, marginLeft: 4 }}>by Sway Integrations</span>
        </div>
        <div style={{ display: 'flex', gap: 2 }}>
          {navBtn('history', 'Dashboard')}
          {navBtn('reports', 'Reports')}
          {!isAdmin && navBtn('new', 'New Payment')}
          {isAdmin && navBtn('admin', 'Admin')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: '#666' }}>{user?.name || user?.email}</span>
          {isAdmin && (
            <span style={{ fontSize: 10, color: '#4ade80', background: '#1a3a2a', padding: '2px 6px', borderRadius: 4, fontFamily: "'DM Mono', monospace" }}>ADMIN</span>
          )}
          <button onClick={handleLogout} style={{ background: 'none', border: '0.5px solid #2a2a2a', color: '#666', fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
            Sign out
          </button>
          <div style={{ background: '#1a3a2a', color: '#4ade80', fontSize: 10, fontFamily: "'DM Mono', monospace", padding: '2px 8px', borderRadius: 4, letterSpacing: '0.5px' }}>● LIVE</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 24px' }}>
        {shopError && (
          <div style={{ color: '#f87171', fontSize: 13, padding: '16px 0' }}>{shopError}</div>
        )}
        {!shopError && (
          <>
            {tab === 'history' && <PaymentHistory shopId={shopId} isAdmin={isAdmin} getToken={getToken} />}
            {tab === 'reports' && <Reports getToken={getToken} />}
            {tab === 'new' && !isAdmin && <NewPayment shopId={shopId} getToken={getToken} />}
            {tab === 'admin' && isAdmin && <Admin getToken={getToken} />}
          </>
        )}
      </div>
    </div>
  );
}
